// ---------------------------------------------------------------------------
// Customers, credit, payments and orders.
//
// Taking a customer payment moves money, so it invalidates the shift caches as
// well as its own. Nothing about an order touches stock or money, so order
// mutations invalidate only orders — claiming otherwise would be a lie the UI
// tells itself.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { PrintPlan } from '../hooks/useSalePrinting';
import { queryKeys } from '../lib/queryClient';
import type {
  Currency,
  Customer,
  CustomerAddress,
  CustomerOrder,
  CustomerPaymentRecord,
  CustomerPurchase,
  CustomerStatement,
  CustomerType,
  OrderFulfilment,
  OrderStatus,
  OutstandingRow,
  Pagination,
  PaymentMethod,
} from '../types';

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function useCustomers(
  options: {
    search?: string;
    type?: CustomerType;
    includeInactive?: boolean;
    onlyWithBalance?: boolean;
    onlyOverdue?: boolean;
  } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['customers', options],
    queryFn: () => {
      const params = new URLSearchParams();
      if (options.search) params.set('search', options.search);
      if (options.type) params.set('type', options.type);
      if (options.includeInactive) params.set('includeInactive', 'true');
      if (options.onlyWithBalance) params.set('onlyWithBalance', 'true');
      if (options.onlyOverdue) params.set('onlyOverdue', 'true');

      return api.get<{ customers: Customer[]; fieldAccess: { creditVisible: boolean } }>(
        `/customers?${params.toString()}`,
      );
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      const response = await api.get<{ customer: Customer }>(`/customers/${id}`);
      return response.customer;
    },
    enabled: Boolean(id),
  });
}

export interface CustomerPayload {
  name: string;
  phone?: string | null;
  altPhone?: string | null;
  email?: string | null;
  type?: CustomerType;
  notes?: string | null;
  isActive?: boolean;
}

function invalidateCustomers(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['customers'] });
  void queryClient.invalidateQueries({ queryKey: ['customer'] });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CustomerPayload) =>
      api.post<{ customer: Customer; message: string }>('/customers', payload),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: CustomerPayload & { id: string }) =>
      api.put<{ customer: Customer; message: string }>(`/customers/${id}`, payload),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/customers/${id}`),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

export interface AddressPayload {
  label: string;
  area?: string | null;
  street?: string | null;
  details?: string | null;
  deliveryInstructions?: string | null;
  isDefault?: boolean;
}

export function useAddAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, ...payload }: AddressPayload & { customerId: string }) =>
      api.post<{ address: CustomerAddress; message: string }>(
        `/customers/${customerId}/addresses`,
        payload,
      ),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useUpdateAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: AddressPayload & { id: string }) =>
      api.put<{ address: CustomerAddress; message: string }>(`/customer-addresses/${id}`, payload),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useDeleteAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/customer-addresses/${id}`),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Credit
// ---------------------------------------------------------------------------

export function useUpdateCreditSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      creditAllowed?: boolean;
      creditLimitUsd?: string;
      creditTermsDays?: number;
    }) => api.put<{ customer: Customer; message: string }>(`/customers/${id}/credit`, payload),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useSetCreditBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, blocked, reason }: { id: string; blocked: boolean; reason?: string | null }) =>
      api.post<{ customer: Customer; message: string }>(`/customers/${id}/credit-block`, {
        blocked,
        reason: reason ?? null,
      }),
    retry: false,
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useStatement(customerId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['customer', customerId, 'statement'],
    queryFn: () => api.get<CustomerStatement>(`/customers/${customerId}/statement`),
    enabled: Boolean(customerId) && enabled,
    staleTime: 15_000,
  });
}

export function usePurchaseHistory(customerId: string | null, page = 1, enabled = true) {
  return useQuery({
    queryKey: ['customer', customerId, 'history', page],
    queryFn: () =>
      api.get<{
        sales: CustomerPurchase[];
        lifetimeSpendUsd?: string;
        pagination: Pagination;
      }>(`/customers/${customerId}/history?page=${page}&pageSize=25`),
    enabled: Boolean(customerId) && enabled,
    staleTime: 15_000,
  });
}

export function useAdjustBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      ...payload
    }: {
      customerId: string;
      amountUsd: string;
      reason: string;
      approvalId: string;
    }) =>
      api.post<{ balanceAfterUsd: string; message: string }>(
        `/customers/${customerId}/adjust`,
        payload,
      ),
    retry: false,
    onSuccess: () => {
      invalidateCustomers(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['customer-credit'] });
    },
  });
}

export function useOutstanding(onlyOverdue = false, enabled = true) {
  return useQuery({
    queryKey: ['customer-credit', 'outstanding', onlyOverdue],
    queryFn: () =>
      api.get<{ rows: OutstandingRow[]; totalOwedUsd: string; totalOverdueUsd: string }>(
        `/customer-credit/outstanding?onlyOverdue=${onlyOverdue ? 'true' : 'false'}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Customer payments
// ---------------------------------------------------------------------------

export function useCustomerPayments(
  filters: { customerId?: string; page?: number } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['customer-payments', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.customerId) params.set('customerId', filters.customerId);
      params.set('page', String(filters.page ?? 1));
      params.set('pageSize', '25');

      return api.get<{
        payments: CustomerPaymentRecord[];
        totalUsd: string;
        pagination: Pagination;
      }>(`/customer-payments?${params.toString()}`);
    },
    enabled,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

/// Cash from a customer physically enters the till, so this refreshes the
/// shift caches too.
export function useReceiveCustomerPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      customerId: string;
      amount: string;
      currency: 'USD' | 'LBP';
      method: 'CASH' | 'VISA' | 'WHISH';
      reference?: string | null;
      note?: string | null;
    }) =>
      api.post<{
        payment: { id: string; paymentNumber: string };
        amountUsd: string;
        balanceAfterUsd: string;
        message: string;
      }>('/customer-payments', payload),
    retry: false,
    onSuccess: () => {
      invalidateCustomers(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['customer-payments'] });
      void queryClient.invalidateQueries({ queryKey: ['customer-credit'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
      void queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export function useOrders(
  filters: {
    status?: OrderStatus;
    fulfilment?: OrderFulfilment;
    customerId?: string;
    paymentStatus?: 'UNPAID' | 'PAID';
    onlyOpen?: boolean;
    /// THE outstanding-accounts question - unpaid, uncancelled deliveries.
    /// Since creation is dispatch, everything it matches has left the shop.
    /// The till's quick panel, its badge and the full board's default tab
    /// all ask exactly this, so no two of them can ever disagree.
    awaitingAccount?: boolean;
    /// One cashier's deliveries - honoured for the owner and managers only.
    cashierId?: string;
    /// One drawer's deliveries (its close gate).
    shiftId?: string;
    page?: number;
  } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['orders', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.fulfilment) params.set('fulfilment', filters.fulfilment);
      if (filters.customerId) params.set('customerId', filters.customerId);
      if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
      if (filters.onlyOpen) params.set('onlyOpen', 'true');
      if (filters.awaitingAccount) params.set('awaitingAccount', 'true');
      if (filters.cashierId) params.set('cashierId', filters.cashierId);
      if (filters.shiftId) params.set('shiftId', filters.shiftId);
      params.set('page', String(filters.page ?? 1));
      params.set('pageSize', '25');

      return api.get<{
        orders: CustomerOrder[];
        counts: Record<string, number>;
        pagination: Pagination;
      }>(`/orders?${params.toString()}`);
    },
    enabled,
    // The kitchen board needs to be current without anybody refreshing it -
    // and so do the till's accounts-out panel and its badge: a driver's
    // return recorded on another screen must reach this one on its own.
    staleTime: 5_000,
    refetchInterval: filters.onlyOpen || filters.awaitingAccount ? 30_000 : false,
    placeholderData: (previous) => previous,
  });
}

export function useOrder(id: string | null) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: async () => {
      const response = await api.get<{ order: CustomerOrder }>(`/orders/${id}`);
      return response.order;
    },
    enabled: Boolean(id),
  });
}

function invalidateOrders(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['orders'] });
  void queryClient.invalidateQueries({ queryKey: ['order'] });
}

/// Dispatch a delivery from the till's Current Order - THE one way a new
/// delivery order starts. The response's plan owes the customer receipt the
/// driver takes, and the preparation tickets unless the cashier skipped them.
export function useDispatchDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      items: Array<{
        productId: string;
        variantId?: string | null;
        quantityMode: 'WEIGHT_KG' | 'COUNT' | 'CUSTOM_AMOUNT';
        quantity: string;
        customAmount?: string;
        note?: string | null;
      }>;
      deliveryFeeAmount: string;
      deliveryFeeCurrency: Currency;
      note?: string | null;
      preparationDispatch?: 'NORMAL' | 'SKIP';
      idempotencyKey: string;
    }) =>
      api.post<{ order: CustomerOrder; message: string; printPlan?: PrintPlan | null }>(
        '/orders/delivery',
        payload,
      ),
    retry: false,
    onSuccess: () => {
      invalidateOrders(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

/// The driver came back with the money. A REAL financial event, settled by
/// the canonical sale engine against the order's frozen snapshot - so it
/// touches the shift, the stock and the sales history all at once.
export function useConfirmOnlineDeliveryFee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, amountLbp }: { orderId: string; amountLbp: string }) => api.post<{ order: CustomerOrder; alreadyConfirmed: boolean; printPlan: PrintPlan | null }>(`/orders/${orderId}/delivery-fee`, { amountLbp }),
    onSuccess: () => invalidateOrders(client),
  });
}

export function useCollectDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      ...payload
    }: {
      orderId: string;
      payments: Array<{
        method: PaymentMethod;
        currency: Currency;
        amount: string;
        reference?: string;
      }>;
      changeCurrency?: Currency;
      /// «بدون إيصال» - only meaningful where settlement prints a receipt.
      skipReceipt?: boolean;
      idempotencyKey: string;
    }) =>
      api.post<{ order: CustomerOrder; sale: import('../types').Sale; printPlan?: PrintPlan | null; message: string }>(
        `/orders/${orderId}/collect`,
        payload,
      ),
    retry: false,
    onSuccess: () => {
      invalidateOrders(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['availability'] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
      void queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}
